// theirs
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useAnimation } from 'framer-motion'

// ours
import { ContentDataModel } from '../../interfaces'
import { eventBus } from '../../utils/event-bus'
import TakeAction from './TakeAction'
import Timeline from './Timeline'
import PhotoGallery from './PhotoGallery'
import ImageAndTextQuote from './ImageAndTextQuote'
import TextOverImage from './TextOverImage'
import TitleTextAndFeatureImage from './TitleTextAndFeatureImage'
import IntroOurStory from './IntroOurStory'
import CallToAction from './CallToAction'
import TextQuoteHighlight from './TextQuoteHighlight'
import TextBesideImage from './TextBesideImage'
import SplitTitleAndTextAndImage from './SplitTitleAndTextAndImage'
import TextOnly from './TextOnly'
import TextAtTheTopOfImage from './TextAtTheTopOfImage'
import IntroStatement from './IntroStatement'

export interface ContentProps {
    className?: string
    testId?: string
    content: ContentDataModel
    inView?: boolean
}

const StyledContent = styled.div`
    padding: 0;
    margin: 0;
    position: relative;
    height: 100%;
    width: 100%;
`

const Content = ({ className, testId, content, inView }: ContentProps) => {
    // local use state for content type
    const [contentId, setContentId] = useState({ message: '' })

    // subscribing to event listener for when content type changes (when new frame is now on viewport)
    useEffect(() => {
        eventBus.on('contentId', (data) => {
            setContentId({ message: data.message })
        })
    }, [contentId.message])

    const animationControls = useAnimation()

    useEffect(() => {
        if (inView) {
            animationControls.start('show')
        }
    }, [animationControls, inView])

    return (
        <StyledContent className={className} data-testid={testId}>
            {content.contentType === 'IntroStatement' && (
                <IntroStatement
                    content={content}
                    animationControls={animationControls}
                />
            )}

            {content.contentType === 'TextAtTheTopOfImage' && (
                <TextAtTheTopOfImage
                    content={content}
                    animationControls={animationControls}
                />
            )}

            {content.contentType === 'TextOnly' && (
                <TextOnly
                    content={content}
                    animationControls={animationControls}
                />
            )}

            {content.contentType === 'SplitTitleAndTextAndImage' && (
                <SplitTitleAndTextAndImage
                    content={content}
                    animationControls={animationControls}
                />
            )}

            {content.contentType === 'TextBesideImage' && (
                <TextBesideImage
                    content={content}
                    animationControls={animationControls}
                />
            )}

            {content.contentType === 'TextQuoteHighlight' && (
                <TextQuoteHighlight
                    content={content}
                    animationControls={animationControls}
                    inView = {inView}
                />
            )}

            {content.contentType === 'CallToAction' && (
                <CallToAction
                    content={content}
                    animationControls={animationControls}
                />
            )}
            {content.contentType === 'IntroOurStory' && (
                <IntroOurStory
                    content={content}
                    animationControls={animationControls}
                />
            )}
            {content.contentType === 'TitleTextAndFeatureImage' && (
                <TitleTextAndFeatureImage
                    content={content}
                    animationControls={animationControls}
                />
            )}
            {content.contentType === 'TextOverImage' && (
                <TextOverImage
                    content={content}
                    animationControls={animationControls}
                />
            )}
            {content.contentType === 'ImageAndTextQuote' && (
                <ImageAndTextQuote content={content} />
            )}
            {content.contentType === 'PhotoGallery' && (
                <PhotoGallery
                    content={content}
                    animationControls={animationControls}
                />
            )}
            {content.contentType === 'Timeline' && (
                <Timeline content={content} />
            )}
            {content.contentType === 'TakeAction' && (
                <TakeAction content={content} />
            )}
        </StyledContent>
    )
}

export default Content
